import { Dropdown } from '@carbon/react';
import classNames from 'classnames';
import type { ProductionDataSource } from 'types/api-responses';
import {
  PRODUCTION_DATA_SOURCE_LABELS,
  PRODUCTION_DATA_SOURCES,
} from './utils';
import style from './production-source-dropdown.module.scss';

interface ProductionSourceDropdownProps {
  /** The selected source. */
  value: ProductionDataSource;
  /** The sources the well has production for; the rest are disabled. */
  availableSources: ProductionDataSource[];
  onChange: (source: ProductionDataSource) => void;
  /** Placement from the caller, such as margins. */
  className?: string;
}

/**
 * Picks the production data source a well's production is read from. Sources
 * the well has no production for are listed but disabled.
 *
 * @param {ProductionSourceDropdownProps} props - The selection, the sources the well has, the change handler and the caller's class.
 * @return {JSX.Element} The dropdown.
 */
export function ProductionSourceDropdown({
  value,
  availableSources,
  onChange,
  className,
}: ProductionSourceDropdownProps) {
  const items = PRODUCTION_DATA_SOURCES.map((source) => ({
    id: source,
    label: PRODUCTION_DATA_SOURCE_LABELS[source],
    disabled: !availableSources.includes(source),
  }));

  return (
    <div className={classNames(style.sourceDropdown, className)}>
      <Dropdown
        id="production-data-source"
        titleText="Production Data Source"
        label=""
        size="lg"
        items={items}
        itemToString={(item) => item?.label ?? ''}
        selectedItem={items.find((item) => item.id === value)}
        onChange={({ selectedItem }) => {
          if (selectedItem) onChange(selectedItem.id);
        }}
      />
    </div>
  );
}
